import { Fragment } from "react";

import { Button, ButtonGroup } from "@bokio/designsystem/components/Button";
import { DayInput } from "@bokio/designsystem/components/DayInput/DayInput";
import { FormWithValidation, TextAreaField } from "@bokio/designsystem/components/Form";
import { asValidatorFunction } from "@bokio/designsystem/components/Form/FormWithValidation/FormWithValidation";
import { RenderRequestError } from "@bokio/designsystem/components/Loading";
import { Modal, ModalContent } from "@bokio/designsystem/components/Modal";
import { RadioField } from "@bokio/designsystem/components/RadioField/RadioField";
import { SG } from "@bokio/designsystem/components/SpacingGroup/SpacingGroup";
import { Heading, Paragraph, Section } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { GeneralLangFactory, OnboardingLangFactory } from "@bokio/lang";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { useRouter } from "@bokio/shared/containers/router/useRouter";
import { getRoute } from "@bokio/shared/route";
import { formatMessage } from "@bokio/shared/utils/format";
import {
	EntityValidator,
	FieldRuleFactory,
	FieldRuleLevel,
	FieldValidator,
	filterByField,
} from "@bokio/shared/validation/entityValidator";
import { trackEvent } from "@bokio/utils/t";

import {
	getPlaceholderForReasonCategory,
	getReasonOptions,
	REASON_CATEGORIES_NEEDING_DETAILS,
} from "./userTrialExtensionModalUtils";

import type { Validator, ValidatorResult } from "@bokio/shared/validation/entityValidator";
import type { FC } from "react";

import BokioUserExtendedTrialReasonCategory = m.Bokio.Common.Billing.Model.BokioUserExtendedTrialReasonCategory;

interface UserTrialExtensionModalProps {
	resolve: () => void;
	extensionDays: number;
}

type UserTrialExtensionModalFormData = {
	reasonCategory: m.Bokio.Common.Billing.Model.BokioUserExtendedTrialReasonCategory | undefined;
	reasonDetails: string | undefined;
	expectedStartDate: m.Day | undefined;
};

class UserTrialExtensionModalFormValidator extends EntityValidator<UserTrialExtensionModalFormData> {
	getRules(formValues: UserTrialExtensionModalFormData): Validator[] {
		const onboardingLang = OnboardingLangFactory();
		const generalLang = GeneralLangFactory();
		const factory = new FieldRuleFactory(generalLang);

		const rules = [
			new FieldValidator(this.propertyOf("reasonCategory"), [
				factory.Required(onboardingLang.UserTrialExtensionModal_Question, FieldRuleLevel.MustFixNow),
			]),
		];

		// Require reasonDetails if category needs details
		if (formValues.reasonCategory && REASON_CATEGORIES_NEEDING_DETAILS.includes(formValues.reasonCategory)) {
			rules.push(
				new FieldValidator(this.propertyOf("reasonDetails"), [
					factory.Required("reasonDetails", FieldRuleLevel.MustFixNow),
				]),
			);
		}

		// Require expectedStartDate if category is NotReadyForAccountingSoftware
		if (formValues.reasonCategory === BokioUserExtendedTrialReasonCategory.NotReadyForAccountingSoftware) {
			rules.push(
				new FieldValidator(this.propertyOf("expectedStartDate"), [
					factory.Required(
						onboardingLang.UserExtendedTrial_NotReadyForAccountingSoftware_ExpectedStartDate_Label,
						FieldRuleLevel.MustFixNow,
					),
					factory.DateFormat(
						onboardingLang.UserExtendedTrial_NotReadyForAccountingSoftware_ExpectedStartDate_Label,
						FieldRuleLevel.MustFixNow,
					),
					factory.DateExists(
						onboardingLang.UserExtendedTrial_NotReadyForAccountingSoftware_ExpectedStartDate_Label,
						FieldRuleLevel.MustFixNow,
					),
					factory.DateGreaterThanOrEqual(
						onboardingLang.UserExtendedTrial_NotReadyForAccountingSoftware_ExpectedStartDate_Label,
						m.Day.today(),
						FieldRuleLevel.MustFixNow,
					),
				]),
			);
		}

		return rules;
	}
}

export const UserTrialExtensionModal: FC<UserTrialExtensionModalProps> = ({ resolve, extensionDays }) => {
	const { companyInfo } = useCompanyInfo();
	const onboardingLang = OnboardingLangFactory();
	const router = useRouter();

	const [execExtendTrial, extendTrialRequest] = useLazyApi(
		proxy.Bokio.Common.Web.Settings.SubscriptionController.UserExtendTrial.Post,
	);

	const handleSubmit = async (data: UserTrialExtensionModalFormData) => {
		if (!data.reasonCategory) {
			return;
		}

		const response = await execExtendTrial(companyInfo.Id, {
			ReasonCategory: data.reasonCategory,
			ReasonDetails: data.reasonDetails,
			ExpectedStartDate: data.expectedStartDate ? data.expectedStartDate.toDate() : undefined,
		});

		if (!response.Success) {
			return;
		}

		trackEvent("UserExtendedTrialOffer", "Extend", "UserTrialExtensionModal_ExtendedSuccessfully");
		resolve();
		router.redirectHard(getRoute("dashboard", { company: companyInfo.Id }));
	};

	const validator = new UserTrialExtensionModalFormValidator();
	const isSubmitting = extendTrialRequest.isLoading;
	const reasonOptions = getReasonOptions();

	return (
		<Modal
			testId="UserTrialExtensionModal_Modal"
			visible={true}
			title={onboardingLang.UserTrialExtensionModal_Heading}
			disableBackgroundClick={true}
			onClose={resolve}
		>
			<ModalContent>
				<FormWithValidation<UserTrialExtensionModalFormData, ValidatorResult>
					validator={asValidatorFunction(validator)}
					initialState={{
						reasonCategory: undefined,
						reasonDetails: undefined,
						expectedStartDate: undefined,
					}}
					onSubmit={handleSubmit}
				>
					{({ formData, setValue, validation }) => {
						const getChildForOption = (optionValue: BokioUserExtendedTrialReasonCategory) => {
							if (REASON_CATEGORIES_NEEDING_DETAILS.includes(optionValue)) {
								return (
									<TextAreaField
										testId="UserTrialExtensionModal_ReasonDetails_TextAreaField"
										labelTestId="UserTrialExtensionModal_ReasonDetails_Label"
										placeholder={getPlaceholderForReasonCategory(optionValue)}
										value={formData.reasonDetails}
										onChange={setValue("reasonDetails")}
										disabled={isSubmitting}
										minRows={3}
										errors={filterByField("reasonDetails", validation.errors || [])}
									/>
								);
							}

							if (optionValue === BokioUserExtendedTrialReasonCategory.NotReadyForAccountingSoftware) {
								return (
									<DayInput
										testId="UserTrialExtensionModal_ExpectedStartDate"
										labelTestId="UserTrialExtensionModal_ExpectedStartDate_Label"
										label={onboardingLang.UserExtendedTrial_NotReadyForAccountingSoftware_ExpectedStartDate_Label}
										value={formData.expectedStartDate ?? undefined}
										onChange={setValue("expectedStartDate")}
										disabled={isSubmitting}
										markAsRequired={true}
										errors={filterByField("expectedStartDate", validation.errors || [])}
									/>
								);
							}

							return undefined;
						};

						return (
							<>
								<SG gap="16">
									<Paragraph>{formatMessage(onboardingLang.UserTrialExtensionModal_Intro, extensionDays)}</Paragraph>

									<Section resetHeadingLevelTo={4}>
										<Heading>{onboardingLang.UserTrialExtensionModal_Question}</Heading>
									</Section>

									{reasonOptions.map(reason => {
										const isSelected = formData.reasonCategory === reason.value;
										return (
											<Fragment key={reason.value}>
												<RadioField
													name="reasonCategory"
													label={reason.label}
													value={reason.value}
													testId={`UserTrialExtensionModal_Reason_${reason.value}`}
													onChange={setValue("reasonCategory")}
													checked={isSelected}
												/>
												{isSelected && getChildForOption(reason.value)}
											</Fragment>
										);
									})}
								</SG>

								<RenderRequestError request={extendTrialRequest} />

								<ButtonGroup margin={["top"]} align="space-between">
									<Button
										testId="UserTrialExtensionModal_BuyButton"
										appearance="secondary"
										type="link"
										route={getRoute("billing", { company: companyInfo.Id }, { from: "UserExtendedTrialModal" })}
										onNavigation={() => {
											resolve();
											trackEvent("UserExtendedTrialOffer", "Click", "UserTrialExtensionModal_BuyButton");
										}}
									>
										{onboardingLang.UserTrialExtensionModal_CTA_BuyNow}
									</Button>
									<Button
										testId="UserTrialExtensionModal_ExtendButton"
										type="submit"
										loading={isSubmitting}
										disabled={validation.errors.length > 0}
									>
										{formatMessage(onboardingLang.UserTrialExtensionModal_CTA_ExtendTrialByXDays, extensionDays)}
									</Button>
								</ButtonGroup>
							</>
						);
					}}
				</FormWithValidation>
			</ModalContent>
		</Modal>
	);
};
